import React, { useState } from 'react';
import { Alert, Button, Col, Icon, Modal, Row, Table, Tag, Tooltip, Upload, message } from 'antd';
import readXlsxFile from 'read-excel-file';
import writeXlsxFile from 'write-excel-file';
import { saveAs } from 'file-saver';
import { BULK_EXPENSE_COLUMNS, BULK_EXPENSE_KEYS } from './expensesByUnit';
import { bulkUpsertExpenses } from '../api/ownerReporting';

const EXPENSE_COLUMNS = BULK_EXPENSE_KEYS;

const REQUIRED_HEADERS = ['servicePackageId', 'servicePackageName', ...EXPENSE_COLUMNS];

const TEMPLATE_SCHEMA = [
  { column: 'servicePackageId', type: String, value: data => data.servicePackageId },
  { column: 'servicePackageName', type: String, value: data => data.servicePackageName },
  ...EXPENSE_COLUMNS.map(key => ({
    column: key,
    type: Number,
    value: data => Number(data[key]) || 0,
  })),
];

const BulkExpensesUploadModal = ({
  visible,
  onCancel,
  onSuccess,
  hostId,
  year,
  month,
  templateRows = [],
  csvName = 'owner-reporting-expenses',
}) => {
  const [loading, setLoading] = useState(false);
  const [validatedData, setValidatedData] = useState([]);
  const [columns, setColumns] = useState([]);

  const resetUpload = () => {
    setValidatedData([]);
    setColumns([]);
  };

  const downloadTemplate = async () => {
    if (!(templateRows || []).length) {
      message.error('No service packages available for template');
      return;
    }
    const blob = await writeXlsxFile(templateRows, { schema: TEMPLATE_SCHEMA });
    saveAs(blob, `${csvName}.xlsx`);
  };

  const parseExcel = async file => {
    const headerRows = await readXlsxFile(file);
    if (!headerRows || !headerRows.length) {
      throw new Error('Invalid Excel file');
    }
    const headers = headerRows[0] || [];
    const schema = {};
    headers.forEach((header, index) => {
      const key = String(header || '').trim();
      if (!key) return;
      schema[key] = {
        prop: key,
        type: index < 2 ? String : Number,
        required: REQUIRED_HEADERS.includes(key),
      };
    });
    return readXlsxFile(file, { schema });
  };

  const handleUploadedTemplate = async info => {
    const file = info && info.file;
    if (!file) return;

    try {
      setLoading(true);
      const output = await parseExcel(file);
      if (!(output.rows || []).length) {
        message.error('Invalid schema or empty file');
        resetUpload();
        return;
      }

      const packageIds = new Set((templateRows || []).map(r => String(r.servicePackageId)));
      const newData = (output.rows || []).map((item, index) => {
        const errorList = [...(output.errors || []).filter(err => err.row === index + 2)];
        const pkgId = String(item.servicePackageId || '');
        if (!pkgId) {
          errorList.push({ column: 'servicePackageId', error: 'is required' });
        } else if (!packageIds.has(pkgId)) {
          errorList.push({ column: 'servicePackageId', error: 'does not match current property packages' });
        }
        EXPENSE_COLUMNS.forEach(key => {
          if (item[key] == null || item[key] === '') {
            item[key] = 0;
          }
          if (Number.isNaN(Number(item[key]))) {
            errorList.push({ column: key, error: 'must be a number' });
          }
        });
        return { ...item, haveError: errorList };
      });

      const extraKeys = Object.keys(newData[0] || {}).filter(
        key => !['haveError', ...REQUIRED_HEADERS].includes(key)
      );

      setValidatedData(newData);
      setColumns([
        {
          title: 'Status',
          width: 110,
          render: row =>
            row.haveError.length === 0 ? (
              <Tag color="green">Accepted</Tag>
            ) : (
              <Tooltip
                title={
                  <div>
                    {row.haveError.map((err, idx) => (
                      <div key={`${err.column}-${idx}`}>
                        {idx + 1}. {err.column} {err.error}
                      </div>
                    ))}
                  </div>
                }
              >
                <Tag color="red">
                  <Icon type="question-circle" /> Error
                </Tag>
              </Tooltip>
            ),
        },
        { title: 'Service Package Id', dataIndex: 'servicePackageId' },
        { title: 'Service Package Name', dataIndex: 'servicePackageName' },
        ...BULK_EXPENSE_COLUMNS.map(col => ({
          title: col.label,
          dataIndex: col.key,
          align: 'right',
        })),
        ...extraKeys.map(key => ({ title: key, dataIndex: key, align: 'right' })),
      ]);
    } catch (e) {
      message.error((e && e.message) || 'Invalid Excel file');
      resetUpload();
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!hostId) {
      message.error('Select a host first');
      return;
    }
    const hasErrors = validatedData.some(row => (row.haveError || []).length > 0);
    if (!validatedData.length || hasErrors) {
      message.error('Fix validation errors before uploading');
      return;
    }

    setLoading(true);
    try {
      const res = await bulkUpsertExpenses(hostId, {
        year,
        month,
        rows: validatedData.map(row => {
          const next = { ...row };
          delete next.haveError;
          return next;
        }),
      });
      message.success((res && res.message) || 'Bulk upload completed');
      resetUpload();
      onCancel();
      if (onSuccess) onSuccess();
    } catch (e) {
      message.error((e && e.message) || 'Bulk upload failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Bulk Expenses Upload"
      visible={visible}
      onOk={handleUpload}
      onCancel={() => {
        resetUpload();
        onCancel();
      }}
      width="75%"
      okText="Upload"
      okButtonProps={{
        disabled: !validatedData.length || validatedData.some(row => (row.haveError || []).length > 0),
        loading,
      }}
      cancelText="Close"
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Excel bulk update"
        description={
          <div>
            <p>
              Download template:{' '}
              <Button type="primary" icon="download" onClick={downloadTemplate}>
                {`${csvName}.xlsx`}
              </Button>
            </p>
            <p style={{ marginBottom: 0 }}>
              1. Download the template and fill in expense amounts. Columns match Create Expenses: Others, Electricity, Water, Internet, Cleaning, Laundry, Service, Check In, Check Out, Toiletry.
              <br />
              2. Extra numeric columns are added into Others. Others and Toiletry are package totals.
              <br />
              3. Save as .xlsx and upload below.
              <br />
              4. Do not remove templated package rows.
              <br />
              Only .xlsx is supported.
            </p>
          </div>
        }
      />

      {validatedData.length === 0 ? (
        <Upload.Dragger beforeUpload={() => false} onChange={handleUploadedTemplate} fileList={[]} accept=".xlsx">
          <p className="ant-upload-drag-icon">
            <Icon type="inbox" style={{ fontSize: 48, color: '#1890ff' }} />
          </p>
          <p className="ant-upload-text">Click or drag .xlsx file to this area to upload</p>
          <p className="ant-upload-hint">Use the downloaded Owner Reporting expenses template only.</p>
        </Upload.Dragger>
      ) : (
        <div>
          <Row style={{ marginBottom: 8 }}>
            <Col>
              <Button onClick={resetUpload}>Choose another file</Button>
            </Col>
          </Row>
          <Table
            bordered
            size="small"
            rowKey={(row, index) => `${row.servicePackageId || 'row'}-${index}`}
            scroll={{ x: true }}
            columns={columns}
            dataSource={validatedData}
            loading={loading}
            pagination={{ pageSize: 20 }}
          />
        </div>
      )}
    </Modal>
  );
};

export default BulkExpensesUploadModal;
